import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import {
  ChevronLeft, Clock, MapPin, Users, ChevronRight, Landmark, UserCircle,
} from 'lucide-react';
import AfterActivityCard from '../components/AfterActivityCard';

const API = process.env.REACT_APP_BACKEND_URL;

/* ============ Shared page shell ============ */
function PageShell({ title, children, testid }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid={testid}>
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-2">
          <button onClick={() => navigate('/backpack')} className="p-2 -ml-2 rounded-full hover:opacity-70" aria-label="Back" data-testid="bs-back">
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>{title}</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5 pb-24">{children}</main>
    </div>
  );
}

/* ============ Activity card row ============ */
function ActivityCardRow({ act, onClick }) {
  const locationStr = typeof act.location === 'string' ? act.location : (act.location?.name || '');
  const statusLabel = (() => {
    const status = (act.lifecycle_status || '').toLowerCase();
    if (status === 'completed') return { text: 'Completed', color: '#6B7280' };
    if (status === 'active') return { text: 'Happening now', color: '#059669' };
    const count = act.participant_count ?? (act.participants || []).length;
    if (count >= 3) return { text: 'Good to go', color: '#059669' };
    if (count >= 1) return { text: `Needs ${3 - count} more`, color: '#D97706' };
    return { text: 'Needs people', color: '#D97706' };
  })();
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors active:scale-[0.99]"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid={`act-row-${act.activity_id}`}
    >
      {act.image_url ? (
        <img src={act.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" loading="lazy" />
      ) : (
        <div className="w-14 h-14 rounded-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--fy-surface-muted) 0%, #E4EADD 100%)' }} />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>{act.title}</p>
        <div className="flex items-center gap-3 text-[11px] mt-0.5" style={{ color: 'var(--fy-text-secondary)' }}>
          {act.start_time && (
            <span className="flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              {new Date(act.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {locationStr && (
            <span className="flex items-center gap-0.5 truncate">
              <MapPin className="w-3 h-3" />
              {locationStr.length > 22 ? locationStr.slice(0, 22) + '...' : locationStr}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${statusLabel.color}15`, color: statusLabel.color }}>
            {statusLabel.text}
          </span>
          {act.my_contribution?.ride === 'can_drive' && <Tag>You offered a ride</Tag>}
          {act.my_contribution?.ride === 'need_ride' && <Tag>You need a ride</Tag>}
          {act.my_contribution?.equipment === 'bringing' && <Tag>You brought equipment</Tag>}
          {act.my_contribution?.equipment === 'needing' && <Tag>You need equipment</Tag>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
    </button>
  );
}
const Tag = ({ children }) => (
  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
    {children}
  </span>
);

/* ============ BACKPACK ACTIVITY ============ */
export function BackpackActivity() {
  const { user, authResolved } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authResolved) return;
    if (!user) { navigate('/auth?mode=signin'); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/activities/my-activities`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setActivities(r.data?.activities || []))
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [user, authResolved, navigate]);

  const now = new Date();
  const hosted = activities.filter(a => a.my_role === 'host');
  const going = activities.filter(a => a.my_role !== 'host' && a.my_signal === 'in' && (!a.start_time || new Date(a.start_time) >= now));
  const interested = activities.filter(a => a.my_role !== 'host' && a.my_signal === 'interested' && (!a.start_time || new Date(a.start_time) >= now));
  const past = activities.filter(a => a.my_role !== 'host' && a.start_time && new Date(a.start_time) < now);
  const total = going.length + interested.length + hosted.length + past.length;

  return (
    <PageShell title="Your Activity" testid="backpack-activity-page">
      {loading ? (
        <p className="text-sm text-center mt-10" style={{ color: 'var(--fy-text-disabled)' }}>Loading...</p>
      ) : total === 0 ? (
        <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>Join something to start building your activity.</p>
      ) : (
        <div className="space-y-6">
          {going.length > 0 && <Group label="Going" acts={going} navigate={navigate} />}
          {interested.length > 0 && <Group label="Interested" acts={interested} navigate={navigate} />}
          {hosted.length > 0 && <Group label="Hosted" acts={hosted} navigate={navigate} />}
          {past.length > 0 && <Group label="Past" acts={past} navigate={navigate} />}
        </div>
      )}
    </PageShell>
  );
}

function Group({ label, acts, navigate }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--fy-text-disabled)' }}>{label}</p>
      <div className="space-y-2">
        {acts.map(a => <ActivityCardRow key={a.activity_id} act={a} onClick={() => navigate(`/activities/${a.activity_id}`)} />)}
      </div>
    </div>
  );
}

/* ============ BACKPACK PEOPLE ============ */
export function BackpackPeople() {
  const { user, authResolved } = useContext(AuthContext);
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authResolved) return;
    if (!user) { navigate('/auth?mode=signin'); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/invites/connections`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setConnections(r.data?.connections || []))
      .catch(() => setConnections([]))
      .finally(() => setLoading(false));
  }, [user, authResolved, navigate]);

  return (
    <PageShell title="People you've done things with" testid="backpack-people-page">
      {loading ? (
        <p className="text-sm text-center mt-10" style={{ color: 'var(--fy-text-disabled)' }}>Loading...</p>
      ) : connections.length === 0 ? (
        <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>
          People you meet through activities appear here.
        </p>
      ) : (
        <div className="space-y-2">
          {connections.map(p => (
            <button
              key={p.user_id}
              onClick={() => navigate(`/u/${p.username || p.user_id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              data-testid={`bp-person-${p.user_id}`}
            >
              <img
                src={p.picture || p.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || 'U')}&background=6B8F71&color=fff&size=64`}
                alt=""
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=U&background=6B8F71&color=fff&size=64`; }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{p.name || p.username || 'User'}</p>
                {p.last_activity_title && (
                  <p className="text-xs truncate" style={{ color: 'var(--fy-text-disabled)' }}>Last together: {p.last_activity_title}</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            </button>
          ))}
        </div>
      )}
    </PageShell>
  );
}

/* ============ BACKPACK MEMORY ============ */
export function BackpackMemory() {
  const { user, authResolved } = useContext(AuthContext);
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authResolved) return;
    if (!user) { navigate('/auth?mode=signin'); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/after/memories?limit=200`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSaved(r.data?.memories || []))
      .catch(() => setSaved([]))
      .finally(() => setLoading(false));
  }, [user, authResolved, navigate]);

  return (
    <PageShell title="Memory" testid="backpack-memory-page">
      {/* Pending "You showed up" cards (always attempt to render; component hides if none) */}
      <div className="-mx-4">
        <AfterActivityCard />
      </div>

      {loading ? (
        <p className="text-sm text-center mt-6" style={{ color: 'var(--fy-text-disabled)' }}>Loading...</p>
      ) : saved.length === 0 ? (
        <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>Moments you keep from activities appear here.</p>
      ) : (
        <div className="space-y-2 mt-3">
          {saved.map(m => {
            const when = m.time ? new Date(m.time) : null;
            return (
              <div
                key={m.memory_id}
                className="p-3 rounded-xl"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                data-testid={`bp-saved-${m.memory_id}`}
              >
                <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{m.title || 'Activity'}</p>
                <div className="flex items-center gap-3 text-[11px] mt-0.5" style={{ color: 'var(--fy-text-secondary)' }}>
                  {when && (
                    <span className="flex items-center gap-0.5">
                      <Clock className="w-3 h-3" />
                      {when.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                  {typeof m.location === 'string' && m.location && (
                    <span className="flex items-center gap-0.5 truncate">
                      <MapPin className="w-3 h-3" />
                      {m.location.length > 26 ? m.location.slice(0, 26) + '...' : m.location}
                    </span>
                  )}
                </div>
                {m.note && <p className="text-xs italic mt-1.5" style={{ color: 'var(--fy-text-secondary)' }}>"{m.note}"</p>}
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}

/* ============ BACKPACK GROUPS ============ */
export function BackpackGroups() {
  const { user, authResolved } = useContext(AuthContext);
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authResolved) return;
    if (!user) { navigate('/auth?mode=signin'); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/groups/my-groups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setGroups(r.data?.groups || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [user, authResolved, navigate]);

  return (
    <PageShell title="Groups" testid="backpack-groups-page">
      {loading ? (
        <p className="text-sm text-center mt-10" style={{ color: 'var(--fy-text-disabled)' }}>Loading...</p>
      ) : groups.length === 0 ? (
        <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>Groups you join appear here.</p>
      ) : (
        <div className="space-y-2">
          {groups.map(g => (
            <button
              key={g.group_id}
              onClick={() => navigate(`/groups/${g.group_id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              data-testid={`bp-group-${g.group_id}`}
            >
              {g.image_url || g.photo ? (
                <img src={g.image_url || g.photo} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted, #EDF1E8)' }}>
                  <Users className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{g.name}</p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{g.member_count || 0} members</p>
              </div>
              <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            </button>
          ))}
        </div>
      )}
    </PageShell>
  );
}

/* ============ BACKPACK COMMONS ============ */
export function BackpackCommons() {
  const { user, authResolved } = useContext(AuthContext);
  const navigate = useNavigate();
  const [commons, setCommons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authResolved) return;
    if (!user) { navigate('/auth?mode=signin'); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/commons/my-commons`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCommons(r.data?.commons || []))
      .catch(() => setCommons([]))
      .finally(() => setLoading(false));
  }, [user, authResolved, navigate]);

  return (
    <PageShell title="Commons" testid="backpack-commons-page">
      {loading ? (
        <p className="text-sm text-center mt-10" style={{ color: 'var(--fy-text-disabled)' }}>Loading...</p>
      ) : commons.length === 0 ? (
        <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>Commons you belong to appear here.</p>
      ) : (
        <div className="space-y-2">
          {commons.map((c, i) => (
            <button
              key={c.commons_id || c.id || i}
              onClick={() => navigate(`/commons/${c.commons_id || c.id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              data-testid={`bp-commons-${c.commons_id || i}`}
            >
              <Landmark className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-earth-brown, #8A7A5C)' }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{c.name}</p>
                {c.description && (
                  <p className="text-[11px] truncate" style={{ color: 'var(--fy-text-disabled)' }}>{c.description}</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            </button>
          ))}
        </div>
      )}
    </PageShell>
  );
}

// Silence unused-icon lint
export const _unused = { UserCircle };
